import managementApi from '@utils/managementApi';
import { CompositeDataItem, Voice } from '../store/reducers/sessionReducer';

export interface NegotiateParams {
  mentorId: string;
  useIdleTimeOut: boolean;
  avatarInfo: {
    id: string;
    useGreenVideo: boolean;
    functions?: string[];
    libraryId?: string;
  };
  userContextId?: string;
  prevMentorId?: string;
  prevSocketId?: string;
  linkedInSummary?: string;
  profileJson?: string;
  initialSpeech?: string;
}

export interface NegotiateResponse {
  connection: {
    signaling_endpoint: string;
    id: string;
    avatar: {
      voices: Voice[];
    };
  };
  socket_id: string;
}

const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const retryWithExponentialBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries = 5,
  initialDelayMs = 2000,
  maxDelayMs = 64000,
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (error: any) {
      lastError = error;
      const is204Status = error?.response?.status === 204;

      if (is204Status && attempt < maxRetries - 1) {
        const delay = Math.min(
          initialDelayMs * Math.pow(2, attempt),
          maxDelayMs,
        );
        await sleep(delay);
        continue;
      }

      throw error;
    }
  }

  throw lastError;
};

const negotiateUrls = {
  default: '/v2/sessions/negotiate-signaling',
  interview: '/v2/sessions/negotiate-interview',
};

export type NegotiateType = 'default' | 'interview';

export async function doNegotiation(
  params: NegotiateParams,
  abortSignal: AbortSignal,
  type: NegotiateType,
): Promise<NegotiateResponse> {
  const answerResponse = await retryWithExponentialBackoff(async () => {
    const response = await managementApi.post<NegotiateResponse>(
      negotiateUrls[type],
      { ...params, useBackChannel: true },
      {
        signal: abortSignal,
      },
    );

    if (response.status === 204) {
      const error: any = new Error('Negotiation returned 204 status');
      error.response = response;
      throw error;
    }

    return response;
  });

  return answerResponse.data;
}
export interface ContextByIdResponse {
  succsess: boolean;
  data: {
    userQuery: string;
    title: string;
    outline: string | null;
    clickbaitHeader: string | null;
    createdAt: number;
    id: string;
    imageUrl: string;
    prompt: CompositeDataItem[];
    status: string;
  };
}

export async function getContextById(id: string): Promise<ContextByIdResponse> {
  const answerResponse = await managementApi.get<ContextByIdResponse>(
    `/v2/contexts/${id}`,
  );

  return answerResponse.data;
}

export async function getScreenshot(url: string): Promise<Blob> {
  const response = await managementApi.post<Blob>(
    `/v2/thumbnails`,
    {
      url,
    },
    { responseType: 'blob' },
  );

  return response.data;
}
